/**
 * AudioBus — Central audio coordinator ("patch cable")
 *
 * Ensures only one audio source plays at a time across the entire platform.
 * Each page/component REGISTERS its pause function, then calls audioBus.claim()
 * when it starts playing. The bus automatically pauses all other registered sources.
 *
 * Usage:
 *   import audioBus from '../services/AudioBus';
 *
 *   // On mount: register this player
 *   const handle = audioBus.register('home-player', {
 *     pause: () => { audioRef.current.pause(); setIsPlaying(false); }
 *   });
 *
 *   // When user clicks play:
 *   handle.claim();  // stops all other players
 *   audioRef.current.play();
 *
 *   // On unmount: unregister
 *   handle.unregister();
 */

class AudioBus {
  constructor() {
    this._sources = new Map();
    this._activeSource = null;
  }

  /**
   * Register an audio source.
   * @param {string} sourceId - Unique ID (e.g., 'home-player', 'whitelabel', 'global-player')
   * @param {object} controls - { pause: () => void }
   * @returns {{ claim: () => void, unregister: () => void }}
   */
  register(sourceId, controls) {
    this._sources.set(sourceId, controls);

    return {
      claim: () => this._claim(sourceId),
      unregister: () => {
        this._sources.delete(sourceId);
        if (this._activeSource === sourceId) this._activeSource = null;
      },
    };
  }

  /**
   * Called when a source starts playing. Pauses all OTHER sources.
   */
  _claim(sourceId) {
    this._sources.forEach((controls, id) => {
      if (id !== sourceId) {
        try { controls.pause(); } catch (e) { /* ignore */ }
      }
    });
    this._activeSource = sourceId;
  }

  /** Stop all sources */
  stopAll() {
    this._sources.forEach((controls) => {
      try { controls.pause(); } catch (e) { /* ignore */ }
    });
    this._activeSource = null;
  }

  /** Get active source ID */
  get active() {
    return this._activeSource;
  }
}

const audioBus = new AudioBus();
export default audioBus;
